import {
  Field,
  FieldProps,
  Input,
  InputOnChangeData,
  makeStyles,
  mergeClasses,
} from "@fluentui/react-components";
import { useEffect, useState } from "react";

import { isVisibleComponetBaseOnRole } from "@/commons/RoleFunction.ts";
import { ETheme, FluentProvider } from "../../";
import { EyeOffRegular, EyeRegular } from "@fluentui/react-icons";

const useStyles = makeStyles({
  root: {
    fontWeight: 600,
  },
  input: {
    "& input::-ms-reveal, & input::-ms-clear": {
      display: "none",
    },
  },
  disabled: {
    backgroundColor: "var(--colorSubtleBackgroundHover)",
    "& input": {
      color: "var(--colorNeutralForeground1)",
    },
  },
  inputNumber: {
    "& input::-webkit-outer-spin-button": {
      display: "none",
    },
    "& input::-webkit-inner-spin-button": {
      display: "none",
    },
  },
});

export interface TextFieldProps {
  rcName?: string;
  theme?: ETheme;
  name?: string;
  value?: string;
  rcRole?: string[];
  requireRoles?: string[];
  requireOwner?: boolean;
  isOwner?: boolean;
  disabled?: boolean;
  appearance?:
    | "outline"
    | "underline"
    | "filled-darker"
    | "filled-lighter"
    | "filled-darker-shadow"
    | "filled-lighter-shadow";
  type?:
    | "text"
    | "email"
    | "password"
    | "search"
    | "tel"
    | "url"
    | "date"
    | "datetime-local"
    | "month"
    | "number"
    | "time"
    | "week";
  onChangeText?: (
    event: React.ChangeEvent<HTMLInputElement>,
    newValue?: string,
  ) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disableAutoComplete?: boolean;
  disablePasswordVisibility?: boolean;
  renderLabel?: () => React.ReactNode;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  size?: "small" | "medium" | "large";
  onShowPassword?: () => void;
  showPassword?: boolean;
  readOnly?: boolean;
  max?: number;
  min?: number;
}

const TextField = (props: TextFieldProps & FieldProps) => {
  const styles = useStyles();
  const [showPassword, setShowPassword] = useState(false);
  const [hoverPassword, setHoverPassword] = useState(false);
  const isCanBeVisible =
    isVisibleComponetBaseOnRole(props.rcRole, props.requireRoles) &&
    (props.requireOwner ? props.isOwner : true);

  useEffect(() => {
    setShowPassword(!!props.showPassword);
  }, [props.showPassword]);

  const onChange = (
    ev: React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ) => {
    if (props.onChangeText) {
      props.onChangeText(ev, data.value);
    }
  };

  const togglePasswordVisibility = (show: boolean) => {
    setShowPassword(show);
  };

  const isDisabled = props.disabled || !isCanBeVisible;

  return props?.theme ? (
    <FluentProvider theme={props.theme}>
      <Field
        label={
          props.renderLabel
            ? {
                children: props.renderLabel(),
              }
            : props.label
        }
        validationMessage={props.validationMessage}
        validationMessageIcon={props.validationMessageIcon}
        validationState={props.validationState}
        required={props.required}
        style={props.style}
        className={`${props.className} ${styles.root}`}
        onMouseEnter={() => setHoverPassword(true)}
        onMouseLeave={() => {
          setHoverPassword(false);
          setShowPassword(false);
        }}
        onFocus={() => setHoverPassword(true)}
      >
        <Input
          id={`input-${props.name}-${props.type || ""}`}
          name={props.name}
          className={mergeClasses(
            props.type === "password" ? styles.input : "",
            isDisabled ? styles.disabled : "",
            props.type === "number" ? styles.inputNumber : "",
          )}
          onChange={onChange}
          onBlur={props.onBlur}
          autoComplete={props.disableAutoComplete ? "off" : undefined}
          value={props.value}
          disabled={isDisabled}
          placeholder={props.placeholder}
          type={props.type === "password" && showPassword ? "text" : props.type}
          contentAfter={
            props.type === "password" &&
            hoverPassword &&
            !props.disablePasswordVisibility ? (
              showPassword ? (
                <EyeOffRegular
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    togglePasswordVisibility(false);
                    if (props.onShowPassword) {
                      props.onShowPassword();
                    }
                  }}
                />
              ) : (
                <EyeRegular
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    togglePasswordVisibility(true);
                    if (props.onShowPassword) {
                      props.onShowPassword();
                    }
                  }}
                />
              )
            ) : undefined
          }
          appearance={props.appearance}
          onKeyDown={props.onKeyDown}
          autoFocus={props.autoFocus}
          size={props.size}
          onFocus={props.onFocus}
          onClick={props.onClick}
          readOnly={props.readOnly}
          max={props.max}
          min={props.min}
        />
      </Field>
    </FluentProvider>
  ) : (
    <Field
      label={
        props.renderLabel
          ? {
              children: props.renderLabel(),
            }
          : props.label
      }
      validationMessage={props.validationMessage}
      validationMessageIcon={props.validationMessageIcon}
      validationState={props.validationState}
      required={props.required}
      style={props.style}
      className={`${props.className} ${styles.root}`}
      onMouseEnter={() => setHoverPassword(true)}
      onMouseLeave={() => {
        setHoverPassword(false);
        setShowPassword(false);
      }}
      onFocus={() => setHoverPassword(true)}
      size={props.size}
    >
      <Input
        id={`input-${props.name}-${props.type || ""}`}
        className={mergeClasses(
          props.type === "password" ? styles.input : "",
          isDisabled ? styles.disabled : "",
          props.type === "number" ? styles.inputNumber : "",
        )}
        name={props.name}
        onChange={onChange}
        onBlur={props.onBlur}
        value={props.value}
        autoComplete={props.disableAutoComplete ? "off" : undefined}
        disabled={isDisabled}
        placeholder={props.placeholder}
        type={props.type === "password" && showPassword ? "text" : props.type}
        contentAfter={
          props.type === "password" &&
          hoverPassword &&
          !props.disablePasswordVisibility ? (
            showPassword ? (
              <EyeOffRegular
                style={{ cursor: "pointer" }}
                onClick={() => {
                  togglePasswordVisibility(false);
                  if (props.onShowPassword) {
                    props.onShowPassword();
                  }
                }}
              />
            ) : (
              <EyeRegular
                style={{ cursor: "pointer" }}
                onClick={() => {
                  togglePasswordVisibility(true);
                  if (props.onShowPassword) {
                    props.onShowPassword();
                  }
                }}
              />
            )
          ) : undefined
        }
        appearance={props.appearance}
        onKeyDown={props.onKeyDown}
        autoFocus={props.autoFocus}
        onFocus={props.onFocus}
        onClick={props.onClick}
        readOnly={props.readOnly}
        max={props.max}
        min={props.min}
      />
    </Field>
  );
};

export default TextField;

//    background-color: var(--colorNeutralBackground5Hover)colorSubtleBackgroundHover;
//color: var(--colorNeutralForeground1);
